import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { firestoreDB } from "../../firebase/firebase-config";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCustomThemeContext } from "../../providers/customThemeProvider";

const AdminPanel: React.FC = () => {
  const { updateTheme } = useCustomThemeContext();

  // Update the theme when the component mounts
  useEffect(() => {
    updateTheme();
  }, []);

  // State to manage the list of users
  const [users, setUsers] = useState<
    { id: string; email: string; roles: string[] }[]
  >([]);
  // State to manage the list of roles
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
  // State to manage the selected user
  const [selectedUser, setSelectedUser] = useState<string>("");
  // State to manage the selected roles for the user
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  // State to manage the new role to be added
  const [newRole, setNewRole] = useState<string>("");
  // State to manage the snackbar visibility
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  // State to manage the snackbar message
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  // Effect to fetch users and roles from Firestore when the component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      const usersCollection = collection(firestoreDB, "users");
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          email: data.email,
          roles: data.roles,
        };
      });
      setUsers(usersList);
    };

    const fetchRoles = async () => {
      const rolesCollection = collection(firestoreDB, "roles");
      const rolesSnapshot = await getDocs(rolesCollection);
      const rolesList = rolesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
        };
      });
      // Sort roles alphabetically by name
      rolesList.sort((a, b) => a.name.localeCompare(b.name));
      setRoles(rolesList);
    };

    fetchUsers();
    fetchRoles();
  }, []);

  // Function to handle role change for a user
  const handleRoleChange = async () => {
    const userDoc = doc(firestoreDB, "users", selectedUser);
    await updateDoc(userDoc, { roles: selectedRoles });
    setSnackbarMessage("Roles updated successfully");
    setOpenSnackbar(true);
    // Refresh the user list to reflect the changes
    const updatedUsers = users.map((user) =>
      user.id === selectedUser ? { ...user, roles: selectedRoles } : user
    );
    setUsers(updatedUsers);
  };

  // Function to handle adding a new role
  const handleAddRole = async () => {
    const rolesCollection = collection(firestoreDB, "roles");
    await addDoc(rolesCollection, { name: newRole });
    setSnackbarMessage("Role added successfully");
    setOpenSnackbar(true);
    // Refresh the roles list to reflect the changes
    const rolesSnapshot = await getDocs(rolesCollection);
    const rolesList = rolesSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
      };
    });
    // Sort roles alphabetically by name
    rolesList.sort((a, b) => a.name.localeCompare(b.name));
    setRoles(rolesList);
    setNewRole("");
  };

  // Function to handle deleting a role
  const handleDeleteRole = async (roleId: string) => {
    const roleDoc = doc(firestoreDB, "roles", roleId);
    await deleteDoc(roleDoc);
    setSnackbarMessage("Role deleted successfully");
    setOpenSnackbar(true);
    // Refresh the roles list to reflect the changes
    const rolesSnapshot = await getDocs(collection(firestoreDB, "roles"));
    const rolesList = rolesSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
      };
    });
    // Sort roles alphabetically by name
    rolesList.sort((a, b) => a.name.localeCompare(b.name));
    setRoles(rolesList);
  };

  // Function to handle role selection for a user
  const handleRoleSelection = (role: string) => {
    setSelectedRoles((prevRoles) =>
      prevRoles.includes(role)
        ? prevRoles.filter((r) => r !== role)
        : [...prevRoles, role]
    );
  };

  // Function to handle closing the snackbar
  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="flex-start"
      style={{ minHeight: "100vh", marginTop: "64px" }}
    >
      <Grid item xs={12} md={8} lg={6}>
        <Box sx={{ padding: 3 }}>
          <Typography variant="h4" gutterBottom align="center">
            Admin Panel
          </Typography>
          <TextField
            select
            label="Select User"
            value={selectedUser}
            onChange={(e) => {
              const userId = e.target.value;
              setSelectedUser(userId);
              const user = users.find((u) => u.id === userId);
              setSelectedRoles(user ? user.roles : []);
            }}
            fullWidth
            margin="normal"
          >
            {users
              .sort((a, b) => a.email.localeCompare(b.email))
              .map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.email} ({user.roles.join(", ")})
                </MenuItem>
              ))}
          </TextField>
          <FormGroup>
            {roles.map((role) => (
              <Box key={role.id} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRoles.includes(role.name)}
                      onChange={() => handleRoleSelection(role.name)}
                    />
                  }
                  label={role.name}
                />
                <IconButton
                  onClick={() => handleDeleteRole(role.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </FormGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRoleChange}
            sx={{ mt: 2 }}
          >
            Update Roles
          </Button>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Add New Role
            </Typography>
            <TextField
              label="New Role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              fullWidth
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddRole}
              sx={{ mt: 2 }}
            >
              Add Role
            </Button>
          </Box>
        </Box>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default AdminPanel;
